import './SendingStatistics.css';

import React, { useCallback, useEffect, useState } from 'react';

import { Config } from '../../../config';
import { CMSService } from '../../../services/CMSService';
import apiClient from '../../../services/apiClient';

interface ReportRow {
  email: string;
  subject: string;
  timestamp: { _seconds: number } | null;
  sent: boolean;
  delivered: boolean;
  bounced: boolean;
  complaint: boolean;
  excluded?: boolean;
  detail: string;
}

interface ReportSummary {
  sent: number;
  delivered: number;
  bounced: number;
  complaints: number;
  delayed: number;
  unsubscribed: number;
}

const todayUTC = () => new Date().toISOString().slice(0, 10);

// Real SES delivery report backed by emailEventsCollection (events flow
// SES -> SNS -> sesEventIngest). Search runs SERVER-side over every
// recipient of the day — results never depend on what fits on screen.
// Note: events exist from 26 July 2026 onward; earlier dates are empty.
const SendingStatistics: React.FC = () => {
  const [date, setDate] = useState(todayUTC());
  const [search, setSearch] = useState('');
  const [bouncedOnly, setBouncedOnly] = useState(false);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [matches, setMatches] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Mailing-list removals done from this screen; keyed by recipient string.
  const [removal, setRemoval] = useState<Record<string, 'removing' | 'removed'>>({});

  const fetchReport = useCallback(async (day: string, term: string, onlyBounced: boolean) => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.instance.post<any>(
        Config.endPoint.getEmailDeliveryReport,
        { date: day, search: term, onlyBounced }
      );
      setSummary(res.data.summary);
      setRows(res.data.results || []);
      setMatches(res.data.matches || 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load the report');
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(todayUTC(), '', false);
  }, [fetchReport]);

  // Remove a bouncing recipient from the mailing list entirely (deletes the
  // subscription-plan entry AND the user account if one exists — covers the
  // imported email-only entries that never appear in the Users window).
  // Today's report row stays (it's history); they stop receiving from the
  // next briefing onward.
  const removeFromMailingList = async (recipient: string) => {
    const ok = window.confirm(
      `Remove ${recipient} from the mailing list?\n\nThey will no longer receive the daily briefing. This cannot be undone.`
    );
    if (!ok) return;
    setRemoval((m) => ({ ...m, [recipient]: 'removing' }));
    try {
      await CMSService.deleteUserAccount({ email: recipient });
      setRemoval((m) => ({ ...m, [recipient]: 'removed' }));
    } catch (e: any) {
      setRemoval((m) => {
        const { [recipient]: _drop, ...rest } = m;
        return rest;
      });
      alert(
        e?.response?.data?.message ||
          'Failed to remove this address. Please try again.'
      );
    }
  };

  const statusChip = (row: ReportRow) => {
    if (row.excluded)
      return <span style={{ color: '#667085', fontWeight: 700 }}>Not sent</span>;
    if (row.bounced)
      return <span style={{ color: '#8B3A3A', fontWeight: 700 }}>Bounced</span>;
    if (row.complaint)
      return <span style={{ color: '#8B3A3A', fontWeight: 700 }}>Complaint</span>;
    if (row.delivered)
      return <span style={{ color: '#1a7f37', fontWeight: 700 }}>Delivered</span>;
    if (row.sent)
      return <span style={{ color: '#9a6700', fontWeight: 700 }}>Sent (awaiting delivery)</span>;
    return <span>—</span>;
  };

  const summaryCard = (label: string, value: number, color: string) => (
    <div
      style={{
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: '12px 20px',
        minWidth: 110,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#667085' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ color: '#161616', fontSize: 24, fontWeight: 700 }}>
        Email Delivery Report
      </h2>
      <p style={{ color: '#667085', fontSize: 14, maxWidth: 640 }}>
        Live per-recipient delivery status from Amazon SES. The search covers
        every recipient of the selected day — type any part of an address
        (e.g. <code>@soros.com</code>) and press Search. Readers who were NOT
        sent the email (unsubscribed, expired, or not on the briefing plan)
        appear in search results with the reason.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '16px 0' }}>
        <input
          type="date"
          value={date}
          max={todayUTC()}
          onChange={(e) => setDate(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6 }}
        />
        <input
          type="text"
          placeholder="Search name or domain…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchReport(date, search, bouncedOnly)}
          style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, width: 260 }}
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            color: '#8B3A3A',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={bouncedOnly}
            onChange={(e) => {
              setBouncedOnly(e.target.checked);
              fetchReport(date, search, e.target.checked);
            }}
          />
          Bounced only
        </label>
        <button
          onClick={() => fetchReport(date, search, bouncedOnly)}
          disabled={loading}
          style={{
            padding: '8px 20px',
            background: '#0b335e',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Search'}
        </button>
      </div>

      {error && <p style={{ color: '#8B3A3A' }}>{error}</p>}

      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {summaryCard('Sent', summary.sent, '#0b335e')}
          {summaryCard('Delivered', summary.delivered, '#1a7f37')}
          {summaryCard('Bounced', summary.bounced, '#8B3A3A')}
          {summaryCard('Complaints', summary.complaints, '#8B3A3A')}
          {summaryCard('Unsubscribed', summary.unsubscribed ?? 0, '#667085')}
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div style={{ color: '#667085', fontSize: 13, marginBottom: 6 }}>
            {matches} recipient{matches === 1 ? '' : 's'} match
            {matches > rows.length ? ` (showing first ${rows.length} — refine the search)` : ''}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#667085', fontSize: 13, textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Recipient</th>
                <th style={{ padding: '6px 8px' }}>Time (UK)</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Detail</th>
                <th style={{ padding: '6px 8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid #eee', fontSize: 14 }}>
                  <td style={{ padding: '8px' }}>{row.email}</td>
                  <td style={{ padding: '8px', color: '#667085' }}>
                    {row.timestamp
                      ? new Date(row.timestamp._seconds * 1000).toLocaleString(
                          'en-GB',
                          {
                            timeZone: 'Europe/London',
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )
                      : '—'}
                  </td>
                  <td style={{ padding: '8px' }}>{statusChip(row)}</td>
                  <td style={{ padding: '8px', color: '#667085', fontSize: 12, maxWidth: 380 }}>
                    {row.detail}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {row.bounced && !row.excluded ? (
                      removal[row.email] === 'removed' ? (
                        <span style={{ color: '#1a7f37', fontWeight: 600, fontSize: 13 }}>
                          Removed ✓
                        </span>
                      ) : (
                        <button
                          onClick={() => removeFromMailingList(row.email)}
                          disabled={removal[row.email] === 'removing'}
                          title="Delete this address from the mailing list (and its account, if any). Stops all future briefings to it."
                          style={{
                            padding: '4px 12px',
                            background: 'transparent',
                            color: '#8B3A3A',
                            border: '1px solid #8B3A3A',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {removal[row.email] === 'removing'
                            ? 'Removing…'
                            : 'Remove from mailing list'}
                        </button>
                      )
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!loading && summary && rows.length === 0 && (
        <p style={{ color: '#667085' }}>
          No matching recipients on {date}. Tracking data exists from 26 July 2026 onward.
        </p>
      )}
    </div>
  );
};

export default SendingStatistics;
